import * as Y from 'yjs';
import type { StoreContext } from '../../domain/store.js';
import { CURRENT_SCHEMA_VERSION } from '../../domain/types.js';
import { ingredientsToKeyedMap } from './001-ingredients-to-keyed-map.js';
import { unsortedToMain } from './002-unsorted-to-main.js';
import { collectionTimestamps } from './003-collection-timestamps.js';
import type { Migration, MigrationResult } from './types.js';

export type { Migration, MigrationResult } from './types.js';
export { deterministicId } from './deterministic-id.js';

/** Ordered by target version. Never reorder or renumber; append only. */
export const migrations: readonly Migration[] = [
  ingredientsToKeyedMap,
  unsortedToMain,
  collectionTimestamps,
];

export const CURRENT_MIGRATION_VERSION = CURRENT_SCHEMA_VERSION;

/**
 * The lowest schemaVersion a client may still *write*. The server advertises this on
 * connect; clients below it drop to read-only rather than pushing shapes the rest of the
 * fleet can no longer interpret. Raised only after telemetry shows the old clients are
 * gone — never in the same release that introduces the new shape.
 */
export const MIN_WRITER_VERSION = 1;

export function documentVersion(doc: Y.Doc): number {
  const value = doc.getMap('meta').get('schemaVersion');
  if (typeof value === 'number') return value;
  // A document with content but no recorded version predates versioning; a genuinely empty
  // one is brand new and will be seeded at the current version.
  return doc.getMap('recipes').size > 0 ? 1 : CURRENT_SCHEMA_VERSION;
}

/**
 * Brings a document up to the current shape.
 *
 * Migrations in a CRDT cannot be stop-the-world: a client that has been offline for a
 * month reconnects and pushes updates in the *old* shape, long after the server thinks the
 * migration is finished. So every migration is written to be re-runnable forever, and this
 * function is safe to call on document open, on every remote update, and from a cron job.
 */
export function runMigrations(
  doc: Y.Doc,
  context: Pick<StoreContext, 'now' | 'userId'>,
): MigrationResult {
  const fromVersion = documentVersion(doc);
  const pending = migrations.filter((m) => m.version > fromVersion);
  if (pending.length === 0) {
    return { fromVersion, toVersion: fromVersion, applied: [] };
  }

  const applied: string[] = [];
  doc.transact(() => {
    const meta = doc.getMap('meta');
    let log = meta.get('migrations');
    if (!(log instanceof Y.Map)) {
      log = new Y.Map<unknown>();
      meta.set('migrations', log);
    }
    for (const migration of pending) {
      migration.migrate(doc);
      (log as Y.Map<unknown>).set(String(migration.version), {
        name: migration.name,
        at: context.now(),
        by: context.userId,
      });
      meta.set('schemaVersion', migration.version);
      applied.push(migration.name);
    }
  }, 'migration');

  return {
    fromVersion,
    toVersion: pending[pending.length - 1]?.version ?? fromVersion,
    applied,
  };
}
